/**
 * Native Android FCM Push Notifications (Capacitor)
 *
 * Called once right after login. On Android this:
 *   1. Shows the Android 13+ OS "Allow notifications?" system dialog
 *   2. Gets the FCM registration token
 *   3. POSTs it to /api/push/native-token so the backend can fan out pushes
 *
 * On non-Android platforms (iOS not yet wired / web) this is a no-op.
 * The existing Web Push / VAPID path (useWebPush + EnableNotificationsPrompt)
 * continues to handle browser and PWA users unchanged.
 */
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { PushNotifications } from '@capacitor/push-notifications';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Mirror the JWT into Capacitor Preferences (SharedPreferences on Android)
 * so the native {@link DeclineCallActivity} can POST /calls/{id}/decline
 * directly from the lock-screen incoming-call notification WITHOUT
 * opening the app or unlocking the phone. Also cleared on logout via
 * {@link clearNativeAuthToken}. Non-Android platforms no-op.
 */
export async function mirrorNativeAuthToken(jwt) {
  if (Capacitor.getPlatform() !== 'android' || !jwt) return;
  try {
    await Preferences.set({ key: 'allure_auth_token', value: jwt });
  } catch (err) {
    console.warn('[FCM] Failed to mirror JWT to native prefs:', err);
  }
}

export async function clearNativeAuthToken() {
  if (Capacitor.getPlatform() !== 'android') return;
  try {
    await Preferences.remove({ key: 'allure_auth_token' });
  } catch { /* noop */ }
}

let _listenersAttached = false;

// Push-tap navigation state ---------------------------------------------------
// Capacitor delivers `pushNotificationActionPerformed` as soon as the JS
// listener registers, which can happen BEFORE React Router mounts on a cold
// start. We buffer the target route until `setFCMPushNavigator(navigate)` is
// called from App.js, then flush it through React Router's real navigate()
// instead of the fragile pushState + PopStateEvent trick.
const PENDING_TAP_ROUTE_SS_KEY = 'allure_pending_fcm_tap_route';
let _pushNavigate = null;
let _pendingTapRoute = null;

function readPendingTapRoute() {
  if (_pendingTapRoute) return _pendingTapRoute;
  try { return window.sessionStorage.getItem(PENDING_TAP_ROUTE_SS_KEY) || null; } catch { return null; }
}

function writePendingTapRoute(route) {
  _pendingTapRoute = route;
  try {
    if (route) window.sessionStorage.setItem(PENDING_TAP_ROUTE_SS_KEY, route);
    else window.sessionStorage.removeItem(PENDING_TAP_ROUTE_SS_KEY);
  } catch { /* noop */ }
}

function isSafeInternalRoute(route) {
  return typeof route === 'string' && route.startsWith('/') && !route.startsWith('//');
}

/**
 * Peek the buffered push-tap deep link without consuming it. Used by
 * post-login routing (SmartRootRouter, completeLogin, UnlockPage) so the
 * route survives the splash → unlock → role-home redirect chain and the
 * user lands on the exact screen the push was pointing at.
 */
export function peekPendingPushTapRoute() {
  const route = readPendingTapRoute();
  return isSafeInternalRoute(route) ? route : null;
}

export function consumePendingPushTapRoute() {
  const route = peekPendingPushTapRoute();
  if (route) writePendingTapRoute(null);
  return route;
}

function hasActiveAuthToken() {
  try { return Boolean(window.localStorage.getItem('token')); } catch { return false; }
}

/**
 * Called from App.js (in the same effect that wires initNativeDeepLinks) once
 * React Router is mounted. Setting the navigator also flushes any push-tap
 * route that was buffered while the app was cold-starting — but ONLY when a
 * session token is already present. Without a token, ProtectedRoute would
 * bounce the push target back to /login and the deep link would be lost;
 * instead we leave the route buffered so SmartRootRouter / completeLogin /
 * UnlockPage pick it up once auth is settled.
 */
export function setFCMPushNavigator(navigate) {
  _pushNavigate = navigate;
  const pending = readPendingTapRoute();
  if (pending && _pushNavigate && hasActiveAuthToken()) {
    writePendingTapRoute(null);
    // Defer one microtask so React Router finishes its own mount effects
    // (SmartRootRouter default-route redirect) before we push the tap route
    // on top. Without this delay the default-route redirect can stomp us.
    setTimeout(() => {
      try { _pushNavigate(pending); } catch { /* noop */ }
    }, 0);
  }
}

function handlePushTapRoute(route) {
  if (!isSafeInternalRoute(route)) return;
  if (_pushNavigate && hasActiveAuthToken()) {
    try {
      _pushNavigate(route);
      return;
    } catch { /* fall through to buffer */ }
  }
  // React Router not mounted yet OR auth not restored — buffer so the
  // post-login flush path (SmartRootRouter / completeLogin / UnlockPage)
  // can honour the push tap once the user is actually signed in.
  writePendingTapRoute(route);
}

async function ensureNotificationChannel() {
  try {
    await PushNotifications.createChannel({
      id: 'default',
      name: 'Allure notifications',
      description: 'Messages, iCal reservations, and alerts',
      importance: 4,   // HIGH
      visibility: 1,   // PUBLIC
      sound: 'default',
      vibration: true,
    });
  } catch {
    // Channel already exists or not supported — safe to ignore
  }
}

async function postTokenToBackend(token, jwt) {
  try {
    await fetch(`${API}/push/native-token`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${jwt}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, platform: 'android' }),
    });
  } catch (err) {
    console.warn('[FCM] Token registration failed:', err);
  }
}

/**
 * Register for FCM push on Android. Idempotent — safe to call on every login.
 * @param {string} jwt  The user's access token for the backend call.
 */
export async function registerFCMPush(jwt) {
  if (Capacitor.getPlatform() !== 'android') return;

  await ensureNotificationChannel();

  // Attach listeners once per app session (survives route changes)
  if (!_listenersAttached) {
    _listenersAttached = true;

    PushNotifications.addListener('registration', async ({ value: token }) => {
      console.info('[FCM] Registered, posting token…');
      const currentJwt = jwt || localStorage.getItem('token');
      if (currentJwt) {
        await postTokenToBackend(token, currentJwt);
        // Same JWT that just registered the FCM device is the one the
        // native DeclineCallActivity will need to POST /calls/{id}/decline
        // from the lock-screen notification. Mirror it into
        // SharedPreferences so it survives app kill.
        await mirrorNativeAuthToken(currentJwt);
      }
    });

    PushNotifications.addListener('registrationError', (err) => {
      console.error('[FCM] Registration error', err);
    });

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      // App is in foreground — notification is silent at OS level.
      // The in-app bell + Socket.IO realtime toast already cover this.
      console.info('[FCM] Foreground notification', notification.title);
    });

    PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
      // User tapped a background/killed notification. `data.route` is the
      // per-type deep link the backend attaches via user_notifications.py
      // (e.g. iCal reservations → "/host/reservations", new_message → "/messages").
      //
      // Robustness: Capacitor can hand us the data payload in several
      // shapes depending on how the notification arrived — object,
      // JSON-encoded string, or alongside Capacitor's injected
      // google.message_id key. Fall through all three so a `/host/chat/XXX`
      // route still routes correctly on real devices.
      let data = notification?.data || {};
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch { data = {}; }
      }
      // Belt-and-suspenders: when the backend forgot/renamed `route` we
      // fall back to `url`, `click_action`, and finally the per-type
      // default. This keeps new_message pushes landing on the chat even
      // if a mis-serialised payload lost the primary field.
      const route = data?.route || data?.url || data?.click_action || null;
      if (!route && data?.type === 'new_message' && data?.conversation_id) {
        // Build a sensible fallback purely from conversation_id + target_role
        // in case all of `route`/`url` are missing — Android GBoard
        // notification tap on brand-new devices sometimes drops string
        // values > 4KB-ish and the backend's `url` has been seen clipped.
        const role = (data?.target_role || 'guest').toLowerCase();
        const prefix = role === 'host' ? '/host' : role === 'venue' ? '/venue' : '/guest';
        handlePushTapRoute(`${prefix}/chat/${data.conversation_id}`);
        return;
      }
      handlePushTapRoute(route);
    });
  }

  // Check / request Android OS permission (shows system dialog on Android 13+)
  let permission = await PushNotifications.checkPermissions();
  if (permission.receive === 'prompt') {
    permission = await PushNotifications.requestPermissions();
  }
  if (permission.receive !== 'granted') {
    console.warn('[FCM] Notifications permission not granted:', permission.receive);
    return;
  }

  // Trigger FCM registration → fires the 'registration' listener above
  await PushNotifications.register();
}
